
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Project } from '../../types';
import { Plus, Pencil, Trash2, X, Save, Loader2 } from 'lucide-react';
import ImageUpload from './ImageUpload';

export default function ProjectManager() {
    const [projects, setProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingProject, setEditingProject] = useState<Partial<Project> | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showcaseCategories, setShowcaseCategories] = useState<{ id: string; label: string }[]>([]);

    useEffect(() => {
        fetchProjects();
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        const { data } = await supabase
            .from('site_content')
            .select('content')
            .eq('section_key', 'projects_showcase')
            .maybeSingle();

        if (data?.content?.showcaseCategories) {
            setShowcaseCategories(data.content.showcaseCategories);
        }
    };

    const fetchProjects = async () => {
        setLoading(true);
        const { data, error } = await supabase
            .from('projects')
            .select('*')
            .order('display_order', { ascending: true });

        if (error) {
            console.error('Error fetching projects:', error);
        } else {
            setProjects(data || []);
        }
        setLoading(false);
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to delete this project?')) return;

        const { error } = await supabase.from('projects').delete().eq('id', id);

        if (error) {
            console.error('Error deleting project:', error);
            alert('Failed to delete project');
        } else {
            setProjects(projects.filter((p) => p.id !== id));
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingProject) return;

        setSaving(true);

        // Format tags if it's a string (from input)
        const projectData = { ...editingProject };
        if (typeof projectData.tags === 'string') {
            projectData.tags = (projectData.tags as string).split(',').map((t: string) => t.trim());
        }

        const { data, error } = await supabase
            .from('projects')
            .upsert(projectData)
            .select()
            .single();

        if (error) {
            console.error('Error saving project:', error);
            alert('Failed to save project');
        } else {
            if (editingProject.id) {
                setProjects(projects.map((p) => (p.id === data.id ? data : p)));
            } else {
                setProjects([...projects, data]);
            }
            setIsModalOpen(false);
            setEditingProject(null);
        }
        setSaving(false);
    };

    const openNewProjectModal = () => {
        setEditingProject({
            title: '',
            description: '',
            image_url: '',
            live_link: '',
            github_link: '',
            tags: [],
            details: { gallery: [] },
            display_order: projects.length + 1
        });
        setIsModalOpen(true);
    };

    const openEditModal = (project: Project) => {
        setEditingProject(project);
        setIsModalOpen(true);
    };

    if (loading) return <div className="flex justify-center p-12"><Loader2 className="animate-spin text-blue-500" /></div>;

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-white">Projects</h2>
                <button
                    onClick={openNewProjectModal}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                    <Plus size={16} />
                    Add Project
                </button>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {projects.map((project) => (
                    <div key={project.id} className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden flex flex-col">
                        <div className="aspect-video bg-gray-800 relative">
                            {project.image_url ? (
                                <img src={project.image_url} alt={project.title} className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-600">No Image</div>
                            )}
                        </div>
                        <div className="p-4 flex-1 flex flex-col">
                            <h3 className="font-semibold text-white text-lg mb-1">{project.title}</h3>
                            <p className="text-gray-400 text-sm line-clamp-2 mb-4 flex-1">{project.description}</p>

                            <div className="flex gap-2 mt-auto pt-4 border-t border-gray-800">
                                <button
                                    onClick={() => openEditModal(project)}
                                    className="flex-1 flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-white py-2 rounded-md text-sm transition-colors"
                                >
                                    <Pencil size={14} /> Edit
                                </button>
                                <button
                                    onClick={() => handleDelete(project.id)}
                                    className="flex items-center justify-center px-3 bg-red-900/20 hover:bg-red-900/40 text-red-400 rounded-md transition-colors"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Modal */}
            {isModalOpen && editingProject && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
                    <div className="bg-gray-900 border border-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center p-6 border-b border-gray-800 sticky top-0 bg-gray-900 z-10">
                            <h3 className="text-lg font-semibold text-white">
                                {editingProject.id ? 'Edit Project' : 'New Project'}
                            </h3>
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-white">
                                <X size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleSave} className="p-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-400 mb-1">Title</label>
                                        <input
                                            type="text"
                                            required
                                            value={editingProject.title || ''}
                                            onChange={(e) => setEditingProject({ ...editingProject, title: e.target.value })}
                                            className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                            placeholder="Project Name"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-400 mb-1">Subtitle</label>
                                        <input
                                            type="text"
                                            value={editingProject.subtitle || ''}
                                            onChange={(e) => setEditingProject({ ...editingProject, subtitle: e.target.value })}
                                            className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                            placeholder="Short catchphrase or subtitle"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-400 mb-1">Short Description (Card)</label>
                                        <textarea
                                            rows={2}
                                            value={editingProject.description || ''}
                                            onChange={(e) => setEditingProject({ ...editingProject, description: e.target.value })}
                                            className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                            placeholder="Brief summary for project cards..."
                                        />
                                    </div>

                                    <div className="pt-4 border-t border-gray-800">
                                        <h4 className="text-sm font-bold text-blue-400  tracking-widest mb-4">Detailed Content</h4>
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1  tracking-wider">The Challenge</label>
                                                <textarea
                                                    rows={3}
                                                    value={editingProject.details?.overview?.challenge || ''}
                                                    onChange={(e) => setEditingProject({
                                                        ...editingProject,
                                                        details: {
                                                            ...editingProject.details,
                                                            overview: { ...editingProject.details?.overview, challenge: e.target.value }
                                                        }
                                                    })}
                                                    className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                                    placeholder="What problem were you solving?"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1  tracking-wider">The Solution</label>
                                                <textarea
                                                    rows={3}
                                                    value={editingProject.details?.overview?.solution || ''}
                                                    onChange={(e) => setEditingProject({
                                                        ...editingProject,
                                                        details: {
                                                            ...editingProject.details,
                                                            overview: { ...editingProject.details?.overview, solution: e.target.value }
                                                        }
                                                    })}
                                                    className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                                    placeholder="How did you solve it?"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1  tracking-wider">Target Audience</label>
                                                <input
                                                    type="text"
                                                    value={editingProject.details?.overview?.targetAudience || ''}
                                                    onChange={(e) => setEditingProject({
                                                        ...editingProject,
                                                        details: {
                                                            ...editingProject.details,
                                                            overview: { ...editingProject.details?.overview, targetAudience: e.target.value }
                                                        }
                                                    })}
                                                    className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                                    placeholder="Who is this for?"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="pt-4 border-t border-gray-800">
                                        <h4 className="text-sm font-bold text-blue-400  tracking-widest mb-4">Project Details</h4>
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1  tracking-wider">Key Features</label>
                                                <textarea
                                                    rows={3}
                                                    value={editingProject.details?.keyFeatures || ''}
                                                    onChange={(e) => setEditingProject({
                                                        ...editingProject,
                                                        details: {
                                                            ...editingProject.details,
                                                            keyFeatures: e.target.value
                                                        }
                                                    })}
                                                    className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                                    placeholder="Enter key features, one per line (e.g.&#10;Real-time dashboard&#10;User authentication&#10;Payment integration)"
                                                />
                                                <p className="text-[10px] text-gray-600 mt-1 italic">One feature per line</p>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1  tracking-wider">My Role</label>
                                                <input
                                                    type="text"
                                                    value={editingProject.details?.myRole || ''}
                                                    onChange={(e) => setEditingProject({
                                                        ...editingProject,
                                                        details: {
                                                            ...editingProject.details,
                                                            myRole: e.target.value
                                                        }
                                                    })}
                                                    className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                                    placeholder="e.g., Full-Stack Developer & UI Designer"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1  tracking-wider">Achievement</label>
                                                <textarea
                                                    rows={2}
                                                    value={editingProject.details?.achievement || ''}
                                                    onChange={(e) => setEditingProject({
                                                        ...editingProject,
                                                        details: {
                                                            ...editingProject.details,
                                                            achievement: e.target.value
                                                        }
                                                    })}
                                                    className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                                    placeholder="e.g., Reduced load time by 60%, served 10K+ users"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="pt-4 border-t border-gray-800">
                                        <h4 className="text-sm font-bold text-purple-400  tracking-widest mb-4">Project Metadata</h4>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1  tracking-wider">Client</label>
                                                <input
                                                    type="text"
                                                    value={editingProject.details?.client || ''}
                                                    onChange={(e) => setEditingProject({
                                                        ...editingProject,
                                                        details: { ...editingProject.details, client: e.target.value }
                                                    })}
                                                    className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                                                    placeholder="e.g., TechCorp Solutions"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1  tracking-wider">Duration</label>
                                                <input
                                                    type="text"
                                                    value={editingProject.details?.duration || ''}
                                                    onChange={(e) => setEditingProject({
                                                        ...editingProject,
                                                        details: { ...editingProject.details, duration: e.target.value }
                                                    })}
                                                    className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                                                    placeholder="e.g., 6 months"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1  tracking-wider">Comp. Date</label>
                                                <input
                                                    type="text"
                                                    value={editingProject.details?.completionDate || ''}
                                                    onChange={(e) => setEditingProject({
                                                        ...editingProject,
                                                        details: { ...editingProject.details, completionDate: e.target.value }
                                                    })}
                                                    className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                                                    placeholder="e.g., March 2024"
                                                />
                                            </div>
                                            <div className="col-span-1">
                                                <label className="block text-xs font-medium text-gray-500 mb-2 tracking-wider">Categories</label>
                                                <div className="bg-gray-800 border-gray-700 rounded-md p-3 max-h-40 overflow-y-auto space-y-2 custom-scrollbar">
                                                    {showcaseCategories.map(cat => (
                                                        <label key={cat.id} className="flex items-center gap-2 cursor-pointer group">
                                                            <input
                                                                type="checkbox"
                                                                checked={
                                                                    Array.isArray(editingProject.details?.categories)
                                                                        ? editingProject.details.categories.includes(cat.label)
                                                                        : editingProject.details?.category === cat.label
                                                                }
                                                                onChange={(e) => {
                                                                    const currentCats = Array.isArray(editingProject.details?.categories)
                                                                        ? [...editingProject.details.categories]
                                                                        : editingProject.details?.category ? [editingProject.details.category] : [];

                                                                    let updatedCats;
                                                                    if (e.target.checked) {
                                                                        updatedCats = [...currentCats, cat.label];
                                                                    } else {
                                                                        updatedCats = currentCats.filter(c => c !== cat.label);
                                                                    }

                                                                    setEditingProject({
                                                                        ...editingProject,
                                                                        details: {
                                                                            ...editingProject.details,
                                                                            categories: updatedCats,
                                                                            category: updatedCats[0] || '' // Fallback for single-cat logic
                                                                        }
                                                                    });
                                                                }}
                                                                className="rounded border-gray-600 bg-gray-900 text-blue-600 focus:ring-blue-500"
                                                            />
                                                            <span className="text-sm text-gray-300 group-hover:text-white transition-colors">{cat.label}</span>
                                                        </label>
                                                    ))}
                                                    {showcaseCategories.length === 0 && (
                                                        <p className="text-xs text-gray-500 italic">No categories defined. Add them in Showcase Manager.</p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="col-span-2">
                                                <label className="block text-xs font-medium text-gray-500 mb-1  tracking-wider">Status</label>
                                                <select
                                                    value={editingProject.details?.status || 'completed'}
                                                    onChange={(e) => setEditingProject({
                                                        ...editingProject,
                                                        details: { ...editingProject.details, status: e.target.value }
                                                    })}
                                                    className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                                                >
                                                    <option value="completed">Completed</option>
                                                    <option value="in-progress">In Progress</option>
                                                    <option value="maintenance">Maintenance</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="pt-4 border-t border-gray-800">
                                        <h4 className="text-sm font-bold text-orange-400  tracking-widest mb-4">Demo Credentials</h4>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1  tracking-wider">Demo User/Email</label>
                                                <input
                                                    type="text"
                                                    value={editingProject.details?.demoCredentials?.user || ''}
                                                    onChange={(e) => setEditingProject({
                                                        ...editingProject,
                                                        details: {
                                                            ...editingProject.details,
                                                            demoCredentials: { ...editingProject.details?.demoCredentials, user: e.target.value }
                                                        }
                                                    })}
                                                    className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                                                    placeholder="demo@example.com"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1  tracking-wider">Demo Password</label>
                                                <input
                                                    type="text"
                                                    value={editingProject.details?.demoCredentials?.password || ''}
                                                    onChange={(e) => setEditingProject({
                                                        ...editingProject,
                                                        details: {
                                                            ...editingProject.details,
                                                            demoCredentials: { ...editingProject.details?.demoCredentials, password: e.target.value }
                                                        }
                                                    })}
                                                    className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                                                    placeholder="password123"
                                                />
                                            </div>
                                        </div>
                                        <p className="text-[10px] text-gray-500 mt-2 italic">Provided for projects that require login to view live demo.</p>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400 mb-1">Live Link</label>
                                            <input
                                                type="url"
                                                value={editingProject.live_link || ''}
                                                onChange={(e) => setEditingProject({ ...editingProject, live_link: e.target.value })}
                                                className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                                placeholder="https://..."
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400 mb-1">GitHub Link</label>
                                            <input
                                                type="url"
                                                value={editingProject.github_link || ''}
                                                onChange={(e) => setEditingProject({ ...editingProject, github_link: e.target.value })}
                                                className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                                placeholder="https://..."
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-400 mb-1">Tags</label>
                                        <input
                                            type="text"
                                            value={Array.isArray(editingProject.tags) ? editingProject.tags.join(', ') : editingProject.tags || ''}
                                            onChange={(e) => setEditingProject({ ...editingProject, tags: e.target.value as unknown as string[] })}
                                            className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                            placeholder="React, TypeScript, Tailwind (comma separated)"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Separate tags with commas</p>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <ImageUpload
                                        value={editingProject.image_url || null}
                                        onChange={(url) => setEditingProject({ ...editingProject, image_url: url })}
                                        folder="projects"
                                    />

                                    <div className="bg-gray-800/50 p-4 rounded-xl border border-gray-700">
                                        <label className="flex items-center gap-3 cursor-pointer group/toggle">
                                            <div className="relative">
                                                <input
                                                    type="checkbox"
                                                    checked={editingProject.is_featured || false}
                                                    onChange={(e) => setEditingProject({ ...editingProject, is_featured: e.target.checked })}
                                                    className="sr-only peer"
                                                />
                                                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </div>
                                            <span className="text-sm font-bold text-gray-300  tracking-widest group-hover/toggle:text-white transition-colors">Featured Project</span>
                                        </label>
                                        <p className="text-[10px] text-gray-500 mt-2 italic">Spotlight this project on the home page and top of showcase.</p>
                                    </div>

                                    <div className="bg-gray-800/50 p-4 rounded-xl border border-gray-700">
                                        <label className="block text-xs font-bold text-gray-400 mb-3  tracking-widest">Display Order</label>
                                        <input
                                            type="number"
                                            value={editingProject.display_order || 0}
                                            onChange={(e) => setEditingProject({ ...editingProject, display_order: parseInt(e.target.value) })}
                                            className="w-full bg-gray-900 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                        />
                                    </div>

                                    <div className="pt-4 border-t border-gray-800">
                                        <h4 className="text-sm font-bold text-cyan-400  tracking-widest mb-4 uppercase">Project Gallery</h4>
                                        <div className="space-y-4">
                                            {/* Existing Gallery Items */}
                                            <div className="grid grid-cols-2 gap-4">
                                                {(editingProject.details?.gallery || []).map((img: any, idx: number) => (
                                                    <div key={idx} className="relative group border border-gray-700 rounded-lg overflow-hidden aspect-video bg-gray-950">
                                                        <img src={img.src} alt={img.alt} className="w-full h-full object-contain" />
                                                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center p-2">
                                                            <p className="text-[10px] text-white font-medium mb-2 truncate w-full text-center">{img.title || 'No Title'}</p>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const newGallery = [...(editingProject.details?.gallery || [])];
                                                                    newGallery.splice(idx, 1);
                                                                    setEditingProject({
                                                                        ...editingProject,
                                                                        details: { ...editingProject.details, gallery: newGallery }
                                                                    });
                                                                }}
                                                                className="p-1.5 bg-red-600 text-white rounded-full hover:bg-red-500 transition-colors"
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Add New Gallery Item */}
                                            <div className="bg-gray-900/50 p-4 rounded-xl border border-gray-800 border-dashed">
                                                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-3">Add New Image</p>
                                                <ImageUpload
                                                    value={null}
                                                    onChange={(url) => {
                                                        if (!url) return;
                                                        const newImg = {
                                                            id: `img-${Date.now()}`,
                                                            src: url,
                                                            alt: editingProject.title || 'Project Screenshot',
                                                            title: '',
                                                            type: 'image'
                                                        };
                                                        const currentGallery = Array.isArray(editingProject.details?.gallery)
                                                            ? [...editingProject.details.gallery]
                                                            : [];
                                                        setEditingProject({
                                                            ...editingProject,
                                                            details: {
                                                                ...editingProject.details,
                                                                gallery: [...currentGallery, newImg]
                                                            }
                                                        });
                                                    }}
                                                    folder={`projects/${editingProject.id || 'new'}/gallery`}
                                                    multiple={true}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-4 flex justify-end gap-3 border-t border-gray-800 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-6 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50"
                                >
                                    {saving ? <Loader2 className="animate-spin h-4 w-4" /> : <Save className="h-4 w-4" />}
                                    Save Project
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
